import {
	Badge,
	Box,
	Button,
	Divider,
	Dropdown,
	Icons,
	Inline,
	joinAttributes,
	RelativeTime,
	Stack,
	Table,
	Text,
	Time,
} from "@sembark-travel/ui/base"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui/list"
import { useLocationQuery } from "@sembark-travel/ui/router"
import { Email } from "../Contacts"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import { DateRangePickerField, SelectField } from "@sembark-travel/ui/form"
import { SelectUsers } from "../Users"
import { $PropertyType } from "utility-types"
import { useEffect, useMemo } from "react"
import { IUser } from "../Auth"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import { TReportActivity } from "./store"

type TFilters = TSearchParams & {
	q?: string
	users?: Array<IUser>
	created_from?: Date
	created_to?: Date
}
type TQueryParams = TSearchParams & {
	q?: string
	users?: Array<string> // in format id_name
	created_from?: string
	created_to?: string
}
function XHR(xhr: XHRInstance) {
	return {
		async get(
			params?: ReturnType<typeof filtersToRequest>
		): Promise<IListResponse<TReportActivity>> {
			return xhr.get(`/report-downloads`, { params }).then((resp) => resp.data)
		},
	}
}
function filtersToQueryParams(filters: TFilters): TQueryParams {
	const { q, created_from, created_to, users } = filters
	const query: TQueryParams = {}

	if (q) {
		query.q = q
	}
	if (created_from) {
		query.created_from = dateToQuery(created_from)
	}
	if (created_to) {
		query.created_to = dateToQuery(created_to)
	}
	if (users) {
		query.users = users.map((u) => `${u.id}_${u.name}`)
	}

	return query
}

function queryParamsToFilters(query: TQueryParams): TFilters {
	const { q, created_from, created_to, users } = query
	const filters: TFilters = {}

	if (q) {
		filters.q = q
	}
	if (created_from) {
		filters.created_from = parseDateFromQuery(created_from)
	}
	if (created_to) {
		filters.created_to = parseDateFromQuery(created_to)
	}
	if (users) {
		filters.users = users.map((filter: string) => {
			return {
				name: filter.split("_")[1],
				id: Number.parseInt(filter.split("_")[0]),
			}
		}) as unknown as $PropertyType<TFilters, "users">
	}
	return filters
}

function filtersToRequest(params: TFilters) {
	const { created_from, created_to, users, ...otherParams } = params
	return {
		...otherParams,
		created_from: created_from
			? dateToUTCString(startOf(created_from, "day"))
			: undefined,
		created_to: created_to
			? dateToUTCString(endOf(created_to, "day"))
			: undefined,
		users: users?.map((u) => u.id),
	}
}

export function ReportActivitiesList() {
	const [query, setQuery] = useLocationQuery<TFilters>({
		toQuery: filtersToQueryParams,
		fromQuery: queryParamsToFilters,
	})

	const [params, setParams] = useSearch<TFilters>({
		...query,
	})

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	return (
		<Search
			initialParams={params}
			title="Report Activities"
			onSearch={(params) => setParams({ ...params, page: 1 })}
			Filters={Filters}
			areAdvancedFiltersApplied={(filters) => {
				return areAdvancedFiltersAppliedDefault(filters)
			}}
		>
			<ListView<TReportActivity>
				pageKey="report-activities-list"
				params={params}
				fetch={(xhr, filters) => {
					const filterParams = filtersToRequest(filters)
					return XHR(xhr).get({ ...filterParams })
				}}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items }) => (
					<Table
						bordered
						responsive
						striped
						headers={[
							"Report Name",
							"Downloaded By",
							"Filter Used",
							"Created At",
							"Download Type",
							"Status",
							" ",
						]}
						rows={items.map((item: TReportActivity) => [
							item.report_name,
							<Inline>
								{joinAttributes(
									<Text textTransform="capitalize">{item.user.name}</Text>,
									<Email value={item.user.email} iconOnly />
								)}
							</Inline>,
							<Box>
								<RenderFiltersApplied item={item} />
							</Box>,
							<Box>
								<RelativeTime value={item.created_at} />
							</Box>,
							<Text textTransform="capitalize">{item.download_type}</Text>,

							<Box>{renderStatusBadge(item.status)}</Box>,
							<Dropdown alignRight>
								<Dropdown.ToggleButton level="tertiary" size="sm">
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									<ReportActivityDetailsDialog item={item}>
										{({ showDetails }) => (
											<Dropdown.MenuItem onClick={showDetails}>
												<Icons.ArrowExpand /> Details
											</Dropdown.MenuItem>
										)}
									</ReportActivityDetailsDialog>
								</Dropdown.Menu>
							</Dropdown>,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

function ReportActivityDetailsDialog({
	children,
	item,
}: Readonly<{
	children: (props: { showDetails: () => void }) => React.ReactNode
	item: TReportActivity
}>) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ showDetails: open })}
			<Dialog open={isOpen} onClose={close} title="Report Activity Details" md>
				<Dialog.Body>
					<ReportActivityDetails item={item} />
				</Dialog.Body>
				<Dialog.Footer>
					<Button onClick={close}>Close</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

function ReportActivityDetails({ item }: { item: TReportActivity }) {
	return (
		<Stack gap="4">
			<Box padding="6" backgroundColor="subtle" rounded="md">
				<Inline justifyContent={"between"}>
					<Stack>
						<Text color={"muted"} fontSize={"sm"} fontWeight="normal">
							Report Name
						</Text>
						<Text fontSize={"base"} textTransform="capitalize">
							{item.report_name}
						</Text>
					</Stack>

					<Stack>
						<Text color={"muted"} fontSize={"sm"} fontWeight="normal">
							Downloaded By
						</Text>
						<Text fontSize={"base"} textTransform="capitalize">
							{item.user.name}
						</Text>
					</Stack>

					<Stack>
						<Text color={"muted"} fontSize={"sm"} fontWeight="normal">
							Download Type
						</Text>
						<Text fontSize={"base"} textTransform="capitalize">
							{item.download_type}
						</Text>
					</Stack>

					<Stack>
						<Text color={"muted"} fontSize={"sm"} fontWeight="normal">
							Status
						</Text>
						{renderStatusBadge(item.status)}
					</Stack>
				</Inline>

				<Divider marginTop={"2"} marginBottom={"2"} />

				<Stack>
					<Text color={"muted"} fontSize={"sm"} fontWeight="normal">
						Filter Used
					</Text>
					<Text fontSize={"base"}>
						{joinAttributes(
							...Object.entries(item.filters).map(
								([key, value]) => `${formatKey(key)}: ${value}`
							)
						)}
					</Text>
				</Stack>
				<Divider marginTop={"2"} marginBottom={"2"} />
				<Stack gap={"1"}>
					<Text color={"muted"} fontSize={"sm"} fontWeight="normal">
						Selected Columns
					</Text>
					<Inline gap={"1"} flexWrap={"wrap"}>
						{item.selected_columns.map((i) => {
							return <Badge key={i}>{i}</Badge>
						})}
					</Inline>
				</Stack>
			</Box>

			<Stack as="ol" listStyleType="disc" paddingLeft="4">
				<Box as="li">
					<Inline gap={"2"}>
						<Text fontWeight="semibold">Timestamp:</Text>

						<Text fontSize={"base"}>
							{item.created_at ? (
								<Time
									timestamp={dateToUTCString(item.created_at)}
									dateFormat
									timeFormat
								/>
							) : (
								<Text color="muted">N/A</Text>
							)}
						</Text>
					</Inline>
				</Box>
				<Box as="li">
					<Inline gap={"2"}>
						<Text fontWeight="semibold">IP Address:</Text>

						<Text fontSize={"base"}>
							{item.ip_address ? (
								item.ip_address
							) : (
								<Text color="muted">N/A</Text>
							)}
						</Text>
					</Inline>
				</Box>

				<Box as="li">
					<Inline gap={"2"}>
						<Text fontWeight="semibold">Platform:</Text>

						<Text fontSize={"base"}>
							{item.platform ? item.platform : <Text color="muted">N/A</Text>}
						</Text>
					</Inline>
				</Box>
				<Box as="li">
					<Inline gap={"2"}>
						<Text fontWeight="semibold">Client:</Text>

						<Text fontSize={"base"}>
							{item.client ? item.client : <Text color="muted">N/A</Text>}
						</Text>
					</Inline>
				</Box>
				<Box as="li">
					<Inline gap={"2"}>
						<Text fontWeight="semibold">Country:</Text>

						<Text fontSize={"base"}>
							{item.country ? item.country : <Text color="muted">N/A</Text>}
						</Text>
					</Inline>
				</Box>
			</Stack>
		</Stack>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField
				select={SelectUsers}
				name="users"
				label="Users"
				multiple
				fetchOnMount
			/>
			<DateRangePickerField
				label="Report-Date Between"
				fromName="created_from"
				toName="created_to"
				rangePresets={["Today", "Yesterday", "Prev 7 Days", "Last Month"]}
			/>
		</Stack>
	)
}

function RenderFiltersApplied({ item }: { item: TReportActivity }) {
	const stats = useMemo(() => {
		const filters = item.filters
		const entries = Object.entries(filters)
		const total = entries.length
		const visibleCount = 4
		const visibleEntries = entries.slice(0, visibleCount)
		const remainingCount = total - visibleCount

		return {
			filters,
			entries,
			total,
			visibleCount,
			visibleEntries,
			remainingCount,
		}
	}, [item])

	const { visibleEntries, remainingCount } = stats

	return (
		<Box>
			{visibleEntries.length > 0 ? (
				joinAttributes(
					...visibleEntries.map(([key, value]) => `${formatKey(key)}: ${value}`)
				)
			) : (
				<Text color="muted">No filters used</Text>
			)}

			{remainingCount > 0 && (
				<ReportActivityDetailsDialog item={item}>
					{({ showDetails }) => (
						<Text
							as="span"
							cursor="pointer"
							color="accent"
							textAlign={"center"}
							onClick={showDetails}
						>
							<Icons.Plus size={"3"} />
							{remainingCount}
						</Text>
					)}
				</ReportActivityDetailsDialog>
			)}
		</Box>
	)
}

function formatKey(Key: string): string {
	const specialCases: Record<string, string> = {
		ip_address: "IP Address",
	}

	if (specialCases[Key]) {
		return specialCases[Key]
	}

	return Key.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ")
}

function renderStatusBadge(status: TReportActivity["status"]) {
	if (status === "downloaded") {
		return (
			<Badge success>
				<Text textTransform="capitalize">{status}</Text>
			</Badge>
		)
	} else if (status === "failed") {
		return (
			<Badge danger>
				<Text textTransform="capitalize">{status}</Text>
			</Badge>
		)
	} else {
		return (
			<Badge warning>
				<Text textTransform="capitalize">{status}</Text>
			</Badge>
		)
	}
}
